import {
  alpha,
  Avatar,
  Badge,
  Box,
  Card,
  CardContent,
  Container,
  Fab,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ArrowBack, Edit, PersonOutline } from "@mui/icons-material";
import FormActions from "components/FormActions";
import { SubmitHandler, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  updateParent as updateParentApi,
  getRegions,
  getCountries,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { stringAvatar } from "utils";
import staticCountries from "components/data";

type Parent = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  mobile: string;
  address: {
    line1: string;
    line2: string;
    city: string;
    country: string;
    postcode: string;
  };
  photo?: string | File;
  settignId: number;
  role: string;
  nurseryId: number;
};

const req = (label: string) => (
  <>
    {label} <span style={{ color: "#d32f2f" }}>*</span>
  </>
);

const EditParent: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const item = location.state as Parent;
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Parent>({
    defaultValues: item,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState<File>();
  const [country, setCountry] = useState(item?.address?.country || "Algeria");
  const [countries, setCountries] = useState<
    { code: string; name: string; displayName: string }[]
  >([]);
  const [regions, setRegions] = useState<
    { code: string; name: string; displayName?: string }[]
  >([]);
  const [regionLabel, setRegionLabel] = useState("City");

  useEffect(() => {
    if (photo) setValue("photo", photo);
  }, [photo, setValue]);

  useEffect(() => {
    getCountries().then((data) =>
      setCountries(
        data.length
          ? data
          : staticCountries.map((c: any) => ({ ...c, displayName: c.name })),
      ),
    );
  }, [i18n.language]);

  useEffect(() => {
    const countryEntry =
      countries.find((c) => c.name === country) ||
      staticCountries.find((c) => c.name === country);
    if (countryEntry) {
      getRegions(countryEntry.code).then((data) => {
        setRegions(data.regions || []);
        setRegionLabel(data.label || "City");
      });
    } else {
      setRegions([]);
      setRegionLabel("City");
    }
  }, [country, countries, i18n.language]);

  if (!item) {
    navigate("/parents");
    return null;
  }

  const onSubmit: SubmitHandler<Parent> = (data) => {
    setSubmitting(true);
    updateParentApi({ info: data, setPass: false })
      .then(() => {
        enqueueSnackbar(t("parents.updated"), { variant: "success" });
        navigate("/parents");
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || err.message, {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={() => navigate("/parents")}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <PersonOutline sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("parents.edit")}
        </Typography>
      </Toolbar>

      <Container sx={{ py: 3, maxWidth: 600 }} maxWidth={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Photo */}
          <Box display="flex" justifyContent="center" mb={3}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                <Fab
                  size="small"
                  component="label"
                  color="primary"
                  sx={{ width: 32, height: 32, minHeight: 32 }}
                >
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      if (e.target.files?.length) setPhoto(e.target.files[0]);
                    }}
                  />
                  <Edit sx={{ fontSize: 16 }} />
                </Fab>
              }
            >
              <Avatar
                src={
                  photo
                    ? URL.createObjectURL(photo)
                    : (item.photo as string | undefined)
                }
                {...(!photo && !item.photo
                  ? stringAvatar(`${item.firstName} ${item.lastName}`, {
                      width: 88,
                      height: 88,
                      fontSize: 32,
                      border: `3px solid ${theme.palette.divider}`,
                    })
                  : {
                      sx: {
                        width: 88,
                        height: 88,
                        fontSize: 32,
                        border: `3px solid ${theme.palette.divider}`,
                      },
                    })}
              />
            </Badge>
          </Box>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("labels.general_info")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.first_name"))}
                    fullWidth
                    size="small"
                    {...register("firstName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.last_name"))}
                    fullWidth
                    size="small"
                    {...register("lastName")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.email"))}
                    fullWidth
                    size="small"
                    {...register("email")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label={req(t("inputs.mobile"))}
                    fullWidth
                    size="small"
                    error={!!errors.mobile}
                    helperText={errors.mobile && t("validation.required")}
                    {...register("mobile", { required: true })}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {t("address")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label={req(t("inputs.address_line1"))}
                    fullWidth
                    size="small"
                    error={!!errors.address?.line1}
                    helperText={
                      errors.address?.line1 && t("validation.required")
                    }
                    {...register("address.line1", { required: true })}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label={t("inputs.address_line2")}
                    fullWidth
                    size="small"
                    {...register("address.line2")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  {regions.length > 0 ? (
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!errors.address?.city}
                    >
                      <InputLabel>{req(regionLabel)}</InputLabel>
                      <Select
                        label={req(regionLabel)}
                        defaultValue={item.address?.city || ""}
                        {...register("address.city", { required: true })}
                        sx={{ borderRadius: 2 }}
                      >
                        {regions.map((r) => (
                          <MenuItem key={r.code} value={r.name}>
                            {r.code} - {r.displayName || r.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.address?.city && (
                        <FormHelperText>
                          {t("validation.required")}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    <TextField
                      label={req(t("inputs.city"))}
                      fullWidth
                      size="small"
                      error={!!errors.address?.city}
                      helperText={
                        errors.address?.city && t("validation.required")
                      }
                      {...register("address.city", { required: true })}
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: 2 },
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label={req(t("inputs.post_code"))}
                    fullWidth
                    size="small"
                    error={!!errors.address?.postcode}
                    helperText={
                      errors.address?.postcode && t("validation.required")
                    }
                    {...register("address.postcode", { required: true })}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{req(t("inputs.country"))}</InputLabel>
                    <Select
                      label={req(t("inputs.country"))}
                      value={country}
                      {...register("address.country", {
                        onChange: (e) => setCountry(e.target.value),
                      })}
                      sx={{ borderRadius: 2 }}
                    >
                      {countries.map((c) => (
                        <MenuItem key={c.code} value={c.name}>
                          {c.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <FormActions
            onCancel={() => navigate("/parents")}
            submitting={submitting}
          />
        </form>
      </Container>
    </>
  );
};

export default EditParent;
